<template>
	<AppLayout :title="event.title" :subtitle="enrollment.user.full_name" :return-url="route('events.enrollments.index')" :return-label="trans('enrollments.title')">
		<div id="show-enrollment-qr-code" class="fixed p-6 top-0 left-0 w-screen h-screen bg-black/30 dark:bg-black/60 hidden justify-center items-start z-1400 modal overflow-y-auto" @click="closeAllModals">
			<div class="relative w-93.75 h-226.5">
				<img class="absolute object-contain w-full h-full" :src="storage('events/ticket.png')" alt="Ticket">
				<div class="absolute m-8.25 w-77.25">
					<QR :value="`${qrPrefix}:${enrollment.id}`" :size="309" :margin="0"/>
				</div>
				<div class="absolute right-29.25 top-102 w-35.5">
					<img v-if="event.image" class="object-cover object-center w-full border rounded-lg" :src="storage(event.image)" alt="">
					<div v-else class="object-cover size-35.5 border rounded-lg flex items-center justify-center">
						<i class="fa-solid fa-image fa-3x"/>
					</div>
				</div>
				<div class="absolute mx-8.25 mt-141.5 w-77.25">
					<div class="flex flex-col items-stretch gap-4">
						<p class="text-center text-aareii-700 font-bold text-3xl">{{ event.title }}</p>
						<p class="text-center text-aareii-700 font-light text-xl">{{ enrollment.user.full_name }}</p>
						<p v-if="event.begins_on && event.ends_on" class="text-center text-aareii-700">{{ dayjs(event.begins_on).format(trans('general.dayjs.date')) }} - {{ dayjs(event.ends_on).format(trans('general.dayjs.date')) }}</p>
						<div v-if="enrollment.early_bird" class="flex justify-center items-center gap-2 text-gray-400">
							<i class="fa-solid fa-dove"/>
							<span>{{ trans('enrollments.early_bird') }}</span>
						</div>
						<p class="text-center text-aareii-700">#{{ enrollment.id }}</p>
					</div>
				</div>
			</div>
		</div>
		<template #actions>
			<PrimaryButton :color="Color.Blue" @click.stop="showModal('show-enrollment-qr-code')">
				<i class="fa-solid fa-qrcode"/>
			</PrimaryButton>
		</template>
		<div class="grid grid-cols-1 lg:grid-cols-9 gap-4">
			<div class="lg:col-span-3">
				<EnrollmentInfo class="sticky top-36" :enrollment="enrollment" :event="event" :pending-purchase-order="pendingPurchaseOrder"/>
			</div>
			<div class="lg:col-span-6 flex flex-col gap-3">
				<EnrollmentSummary
					:event="event"
					:enrollment="enrollment"
					:selected-packs="selectedPacks"
					:selected-offers="selectedOffers"
					:enrollment-activities="enrollmentActivities"
				/>
				<EnrollmentPurchaseOrders :enrollment="enrollment" :purchase-orders="purchaseOrders"/>
				<EnrollmentPacks
					v-if="!enrollment.is_rejected"
					:enrollment="enrollment"
					:selected-packs="selectedPacks"
					:selection-stage="selectionStage"
					:allowed-to-add-packs="allowedToAddPacks"
					:selection-stage-pack-ids="selectionStagePackIds"
					:selected-activities-by-pack="selectedActivitiesByPack"
					:total-activities-by-pack="totalActivitiesByPack"
					:allowed-to-select-activities="allowedToSelectActivities"
				/>
				<EnrollmentBundles
					v-if="!enrollment.is_rejected"
					:bundles="bundles"
					:enrollment="enrollment"
					:shopping-stage="shoppingStage"
					:allowed-to-add-offers="allowedToAddOffers"
					:total-offers-by-bundle="totalOffersByBundle"
					:shopping-stage-bundle-ids="shoppingStageBundleIds"
					:selected-offers-by-bundle="selectedOffersByBundle"
				/>
				<Card v-if="event.description" class="gap-0">
					<article class="wysiwyg-content" v-html="event.description"/>
				</Card>
			</div>
		</div>
	</AppLayout>
</template>
<script setup lang="ts">
import dayjs from 'dayjs'
import {route} from 'ziggy-js'
import QR from '@/Components/QR.vue'
import {trans} from 'laravel-vue-i18n'
import {type User} from '@/Types/User'
import {Color} from '@/Types/Enums/Color'
import Card from '@/Components/Cards/Card.vue'
import {defineProps, ref, type Ref} from 'vue'
import {type Event} from '@/Types/Events/Event'
import AppLayout from '@/Layouts/AppLayout.vue'
import {type Pack} from '@/Types/Events/Packs/Pack'
import {type Bundle} from '@/Types/Events/Bundles/Bundle'
import {closeAllModals, showModal, storage} from '@/helpers'
import {type Offer} from '@/Types/Events/Bundles/Offers/Offer'
import PrimaryButton from '@/Components/Buttons/PrimaryButton.vue'
import {type Enrollment} from '@/Types/Events/Enrollments/Enrollment'
import {type ShoppingStage} from '@/Types/Events/Stages/ShoppingStage'
import {type SelectionStage} from '@/Types/Events/Stages/SelectionStage'
import EnrollmentInfo from '@/Components/Enrollments/EnrollmentInfo.vue'
import EnrollmentPacks from '@/Components/Enrollments/EnrollmentPacks.vue'
import EnrollmentBundles from '@/Components/Enrollments/EnrollmentBundles.vue'
import EnrollmentSummary from '@/Components/Enrollments/EnrollmentSummary.vue'
import {type EnrollmentActivity} from '@/Types/Events/Enrollments/EnrollmentActivity'
import {type PurchaseOrder} from '@/Types/Events/Enrollments/PurchaseOrders/PurchaseOrder'
import EnrollmentPurchaseOrders from '@/Components/Enrollments/EnrollmentPurchaseOrders.vue'

const props = defineProps<{
	user:User,
	event:Event,
	qrPrefix:string,
	enrollment:Enrollment,
	selectedPacks:Array<Pack>,
	selectedOffers:Array<Offer>,
	allowedToAddPacks:boolean,
	selectionStage:SelectionStage,
	allowedToSelectActivities:boolean,
	pendingPurchaseOrder?:PurchaseOrder,
	purchaseOrders:Array<PurchaseOrder>,
	selectionStagePackIds:Array<number>,
	totalActivitiesByPack:Array<number>,
	selectedActivitiesByPack:Array<number>,
	enrollmentActivities:Array<Array<EnrollmentActivity>>,
	bundles:Array<Bundle>,
	shoppingStage:ShoppingStage,
	allowedToAddOffers:boolean,
	totalOffersByBundle:Array<number>,
	shoppingStageBundleIds:Array<number>,
	selectedOffersByBundle:Array<number>,
}>()

const event:Ref<Event> = ref(props.event)
const qrPrefix:Ref<string> = ref(props.qrPrefix)
const enrollment:Ref<Enrollment> = ref(props.enrollment)
const selectedPacks:Ref<Array<Pack>> = ref(props.selectedPacks)
const allowedToAddPacks:Ref<boolean> = ref(props.allowedToAddPacks)
const selectionStage:Ref<SelectionStage> = ref(props.selectionStage)
const purchaseOrders:Ref<Array<PurchaseOrder>> = ref(props.purchaseOrders)
const totalActivitiesByPack:Ref<Array<number>> = ref(props.totalActivitiesByPack)
const allowedToSelectActivities:Ref<boolean> = ref(props.allowedToSelectActivities)
const selectedActivitiesByPack:Ref<Array<number>> = ref(props.selectedActivitiesByPack)
const bundles:Ref<Array<Bundle>> = ref(props.bundles)
const shoppingStage:Ref<ShoppingStage> = ref(props.shoppingStage)
const allowedToAddOffers:Ref<boolean> = ref(props.allowedToAddOffers)
const totalOffersByBundle:Ref<Array<number>> = ref(props.totalOffersByBundle)
const shoppingStageBundleIds:Ref<Array<number>> = ref(props.shoppingStageBundleIds)
const selectedOffersByBundle:Ref<Array<number>> = ref(props.selectedOffersByBundle)
</script>